import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { spring } from '../../lib/motion'
import styles from './Toast.module.css'

export type ToastVariant = 'success' | 'error' | 'info'

interface ToastSpec {
  id: number
  message: string
  variant: ToastVariant
  duration: number
}

interface ShowToastOptions {
  message: string
  variant?: ToastVariant
  duration?: number
}

interface ToastContextValue {
  show: (opts: ShowToastOptions) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION = 3000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastSpec[]>([])
  const nextIdRef = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id))
  }, [])

  const show = useCallback((opts: ShowToastOptions) => {
    const id = nextIdRef.current++
    const toast: ToastSpec = {
      id,
      message: opts.message,
      variant: opts.variant ?? 'info',
      duration: opts.duration ?? DEFAULT_DURATION
    }
    setToasts((ts) => [...ts, toast])
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.toastWrapper} role="region" aria-live="polite">
        <AnimatePresence>
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>')
  return ctx
}

function ToastItem({ toast, onDismiss }: { toast: ToastSpec; onDismiss: () => void }) {
  // Iter 2026-05-16 audit: `onDismiss` se recrea en cada render del
  // padre (es una arrow `() => dismiss(t.id)` dentro del `.map()`).
  // El effect viejo lo tenía como dep, así que cualquier re-render del
  // padre (ej: aparece otro toast) cancelaba el timer y reseteaba el
  // contador → el toast se quedaba pegado para siempre. Usamos un ref
  // estable para el callback y corremos el timer SÓLO al montar.
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    if (toast.duration <= 0) return
    const timer = setTimeout(() => onDismissRef.current(), toast.duration)
    return () => clearTimeout(timer)
  }, [toast.duration])

  return (
    <motion.div
      className={`${styles.toast} ${styles[toast.variant]}`}
      onClick={onDismiss}
      role="alert"
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.98 }}
      transition={spring.soft}
      layout
    >
      <span className={styles.message}>{toast.message}</span>
    </motion.div>
  )
}
